'use client'

import * as React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight, ChevronDown } from 'lucide-react'
import { PageSpeedGauge } from '@/components/marketing/pagespeed-gauge'

// Real PageSpeed Insights result for a live Idukki widget, framed to read
// like the actual Google tool (gauge rings coloured by score band, the
// 0-49 / 50-89 / 90-100 legend, "values are estimated" microcopy). The
// scores are a real, dated measurement of the live widget URL below; the
// "run it yourself" link reproduces it on pagespeed.web.dev.
//
// Source of truth: PageSpeed Insights, Desktop, widget.idukki.io widget.
// Update SCORES + CWV + MEASURED when the team re-runs it.
const SCORES: { label: string; value: number }[] = [
  { label: 'Performance', value: 99 },
  { label: 'Accessibility', value: 100 },
  { label: 'Best Practices', value: 100 },
  { label: 'SEO', value: 100 },
]
const MEASURED = '12 Jun 2026'

// Core Web Vitals from the same run.
const CWV = [
  { label: 'FCP', value: '0.4 s' },
  { label: 'LCP', value: '0.9 s' },
  { label: 'TBT', value: '0 ms' },
  { label: 'CLS', value: '0' },
  { label: 'Speed Index', value: '1.0 s' },
]

// The exact live widget PageSpeed was run against, so "run it yourself"
// reproduces the same number.
const WIDGET_URL =
  'https://widget.idukki.io/?guid=f06515c9-7c06-4c54-afae-109cf1a9e8e0&bguid=e0054b6e-96ba-4d3e-9785-1ad235df78a7'
const PAGESPEED_URL = `https://pagespeed.web.dev/analysis?url=${encodeURIComponent(WIDGET_URL)}&form_factor=desktop`

// The actual PSI screenshot, shown as proof alongside the recreated gauges.
// To enable: drop the WebP export at public/real/pagespeed-desktop-99.webp
// and set SCREENSHOT_SRC to '/real/pagespeed-desktop-99.webp'.
// A second CWV-detail screenshot can go at public/real/pagespeed-desktop-cwv.webp.
// Empty by default so the live page never renders a broken image before the
// asset is committed.
const SCREENSHOT_SRC = '/real/pagespeed-desktop-99.webp'
const SCREENSHOT_CWV_SRC = '/real/pagespeed-desktop-cwv.webp'

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rotate-45" style={{ backgroundColor: color }} aria-hidden />
      <span>{label}</span>
    </span>
  )
}

function PsiHeader() {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-5 py-3">
      {/* PSI speedometer mark */}
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
        <circle
          cx="12"
          cy="13"
          r="8"
          fill="none"
          stroke="#4285F4"
          strokeWidth="2"
          strokeDasharray="38 12"
          transform="rotate(135 12 13)"
        />
        <circle cx="12" cy="13" r="1.6" fill="#4285F4" />
        <line
          x1="12"
          y1="13"
          x2="16"
          y2="9"
          stroke="#4285F4"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <span className="text-sm font-semibold tracking-tight">PageSpeed Insights</span>
      <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-[#0cce6b]" />
        widget.idukki.io · Desktop
      </span>
    </div>
  )
}

// Full report content, expanded in place below the gauges: CWV strip,
// real PSI screenshots, the explainer paragraph, and the "run it
// yourself" link.
function FullReportBody() {
  return (
    <>
      {/* Core Web Vitals strip */}
      <ul className="mx-auto mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 rounded-xl border bg-muted/30 px-5 py-3.5">
        {CWV.map((m) => (
          <li key={m.label} className="flex flex-col items-center gap-0.5">
            <span className="font-display text-sm font-semibold tabular-nums text-foreground">
              {m.value}
            </span>
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {m.label}
            </span>
          </li>
        ))}
      </ul>

      {/* Real PSI screenshots. Hidden until assets are committed to public/real/. */}
      {(SCREENSHOT_SRC || SCREENSHOT_CWV_SRC) && (
        <div
          className={`mx-auto mt-6 grid gap-4 ${SCREENSHOT_SRC && SCREENSHOT_CWV_SRC ? 'max-w-2xl grid-cols-2' : 'max-w-md grid-cols-1'}`}
        >
          {SCREENSHOT_SRC && (
            <figure>
              <a
                href={SCREENSHOT_SRC}
                target="_blank"
                rel="noreferrer"
                className="group block overflow-hidden rounded-xl border bg-card shadow-sm transition hover:shadow-md"
              >
                <Image
                  src={SCREENSHOT_SRC}
                  alt={`Google PageSpeed Insights desktop report for the Idukki widget — ${SCORES[0].value} Performance, measured ${MEASURED}`}
                  width={960}
                  height={540}
                  sizes="(max-width: 672px) 100vw, 336px"
                  className="w-full"
                />
              </a>
              <figcaption className="mt-2 text-[11px] text-muted-foreground">
                PageSpeed Insights report ({MEASURED}). Tap to open full size.
              </figcaption>
            </figure>
          )}
          {SCREENSHOT_CWV_SRC && (
            <figure>
              <a
                href={SCREENSHOT_CWV_SRC}
                target="_blank"
                rel="noreferrer"
                className="group block overflow-hidden rounded-xl border bg-card shadow-sm transition hover:shadow-md"
              >
                <Image
                  src={SCREENSHOT_CWV_SRC}
                  alt={`Core Web Vitals detail from PageSpeed Insights — FCP 0.4s, LCP 0.9s, TBT 0ms, CLS 0, measured ${MEASURED}`}
                  width={960}
                  height={540}
                  sizes="(max-width: 672px) 100vw, 336px"
                  className="w-full"
                />
              </a>
              <figcaption className="mt-2 text-[11px] text-muted-foreground">
                Core Web Vitals detail ({MEASURED}).
              </figcaption>
            </figure>
          )}
        </div>
      )}

      <p className="mx-auto mt-6 max-w-lg text-sm text-muted-foreground md:text-base">
        A production Idukki gallery on widget.idukki.io: a 37 KB lazy-loaded runtime with 0 ms
        blocking time and CLS 0 that does not move your Core Web Vitals. The widget earns the score,
        not this page.
      </p>
      <Link
        href={PAGESPEED_URL}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
      >
        Run it yourself on PageSpeed <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </>
  )
}

export function PageSpeedProof({ className }: { className?: string }) {
  const [open, setOpen] = React.useState(false)

  return (
    <section className={`container-wide ${className ?? ''}`}>
      <div className="mx-auto max-w-3xl overflow-hidden rounded-3xl border bg-card shadow-sm">
        <PsiHeader />

        <div className="px-6 py-8 text-center md:px-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
            Zero site-speed impact
          </p>
          <h2 className="mt-2 text-balance font-display text-2xl font-semibold tracking-tight md:text-3xl">
            99/100 on a live production widget.
          </h2>

          <ul className="mx-auto mt-7 grid max-w-xl grid-cols-2 gap-7 sm:grid-cols-4 sm:gap-4">
            {SCORES.map((s) => (
              <li key={s.label} className="flex flex-col items-center gap-2">
                <PageSpeedGauge value={s.value} />
                <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
              </li>
            ))}
          </ul>

          {/* Google's score legend + the standard estimate disclaimer */}
          <div className="mx-auto mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <LegendDot color="#ff4e42" label="0–49" />
            <LegendDot color="#ffa400" label="50–89" />
            <LegendDot color="#0cce6b" label="90–100" />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Values are estimated and may vary. Measured {MEASURED} on the live widget.
          </p>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="psi-full-report"
            className="mt-6 inline-flex items-center gap-1.5 rounded-full border bg-background px-5 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:border-primary/40 hover:bg-secondary"
          >
            {open ? 'Hide full report' : 'View full report'}
            <ChevronDown
              className={`motion-safe:ease-[var(--ease-spring)] h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Inline expand: grid-rows trick gives a smooth height
              animation without measuring the content. */}
          <div
            id="psi-full-report"
            className={`duration-[280ms] motion-safe:ease-[var(--ease-spring)] grid transition-[grid-template-rows] ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
          >
            <div className="overflow-hidden">
              <FullReportBody />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
